import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/ReturnDetail.css";

export default function ReturnDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [returnRequest, setReturnRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadReturn();
  }, [id]);

  const loadReturn = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/returns/${id}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("RETURN_LOAD_FAILED");
      }

      setReturnRequest(await response.json());
    } catch {
      setError(t("returns.detailLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const timeline = useMemo(() => {
    if (!returnRequest) {
      return [];
    }

    const currentStatus = returnRequest.status;

    const approvedStatuses = [
      "APPROVED",
      "AWAITING_SHIPMENT",
      "RECEIVED",
      "REFUND_PENDING",
      "REFUNDED",
    ];

    const receivedStatuses = [
      "RECEIVED",
      "REFUND_PENDING",
      "REFUNDED",
    ];

    return [
      {
        key: "REQUESTED",
        label: t("returns.timeline.requested"),
        date: returnRequest.requestedAt,
        completed: Boolean(returnRequest.requestedAt),
      },
      {
        key: "APPROVED",
        label: t("returns.timeline.approved"),
        date: returnRequest.approvedAt,
        completed: approvedStatuses.includes(currentStatus),
      },
      {
        key: "RECEIVED",
        label: t("returns.timeline.received"),
        date: returnRequest.receivedAt,
        completed: receivedStatuses.includes(currentStatus),
      },
      {
        key: "REFUNDED",
        label: t("returns.timeline.refunded"),
        date: returnRequest.refundedAt,
        completed: currentStatus === "REFUNDED",
      },
    ];
  }, [returnRequest, t]);

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    return new Date(value).toLocaleString();
  };

  const formatAmount = (value) => {
    return Number(value || 0).toFixed(2);
  };

  if (loading) {
    return (
      <main className="return-detail-page">
        <section className="return-detail-loading">
          {t("returns.loading")}
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="return-detail-page">
        <section className="return-detail-error">
          <p>{error}</p>

          <button type="button" onClick={loadReturn}>
            {t("returns.retry")}
          </button>
        </section>
      </main>
    );
  }

  if (!returnRequest) {
    return null;
  }

  return (
    <main className="return-detail-page">
      <section className="return-detail-container">
        <button
          type="button"
          className="profile-back-btn"
          onClick={() => navigate("/returns")}
        >
          ← {t("returns.backToReturns")}
        </button>

        <div className="return-detail-heading">
          <div>
            <h1>{returnRequest.returnNumber}</h1>

            <p>
              {t("returns.orderLabel")}:{" "}
              <button
                type="button"
                className="return-detail-order-link"
                onClick={() =>
                  navigate(`/orders/${returnRequest.orderId}`)
                }
              >
                {returnRequest.orderNumber}
              </button>
            </p>
          </div>

          <span
            className={`return-detail-status ${returnRequest.status
              ?.toLowerCase()
              .replaceAll("_", "-")}`}
          >
            {t(`returns.status.${returnRequest.status}`)}
          </span>
        </div>

        <section className="return-detail-summary">
          <article>
            <span>{t("returns.requestDate")}</span>
            <strong>{formatDate(returnRequest.requestedAt)}</strong>
          </article>

          <article>
            <span>{t("returns.refundAmount")}</span>
            <strong>
              {formatAmount(returnRequest.refundAmount)} €
            </strong>
          </article>

          <article>
            <span>{t("returns.products")}</span>
            <strong>{returnRequest.items?.length || 0}</strong>
          </article>

          <article>
            <span>{t("returns.currentStatus")}</span>
            <strong>
              {t(`returns.status.${returnRequest.status}`)}
            </strong>
          </article>
        </section>

        <section className="return-detail-block">
          <h2>{t("returns.reason")}</h2>

          <p>
            {returnRequest.reason
              ? t(`returns.reasons.${returnRequest.reason}`, {
                  defaultValue: returnRequest.reason,
                })
              : "—"}
          </p>
        </section>

        <section className="return-detail-block">
          <h2>{t("returns.comments")}</h2>

          <p>{returnRequest.customerComments || "—"}</p>
        </section>

        {returnRequest.adminComments && (
          <section className="return-detail-block return-detail-admin-comments">
            <h2>{t("returns.adminResponse")}</h2>
            <p>{returnRequest.adminComments}</p>
          </section>
        )}

        {returnRequest.status === "APPROVED" && (
          <section className="return-detail-notice approved">
            <h2>{t("returns.approvedNoticeTitle")}</h2>
            <p>{t("returns.approvedNoticeText")}</p>
          </section>
        )}

        {returnRequest.status === "AWAITING_SHIPMENT" && (
          <section className="return-detail-notice awaiting">
            <h2>{t("returns.awaitingShipmentNoticeTitle")}</h2>
            <p>{t("returns.awaitingShipmentNoticeText")}</p>
          </section>
        )}

        {returnRequest.status === "REFUND_PENDING" && (
          <section className="return-detail-notice pending-refund">
            <h2>{t("returns.refundPendingNoticeTitle")}</h2>
            <p>{t("returns.refundPendingNoticeText")}</p>
          </section>
        )}

        {returnRequest.status === "REFUNDED" && (
          <section className="return-detail-notice refunded">
            <h2>{t("returns.refundedNoticeTitle")}</h2>
            <p>
              {t("returns.refundedNoticeText", {
                amount: `${formatAmount(
                  returnRequest.refundAmount
                )} €`,
              })}
            </p>
          </section>
        )}

        {returnRequest.status === "REJECTED" && (
          <section className="return-detail-notice rejected">
            <h2>{t("returns.rejectedNoticeTitle")}</h2>
            <p>{t("returns.rejectedNoticeText")}</p>
          </section>
        )}

        <section className="return-detail-block">
          <h2>{t("returns.returnedProducts")}</h2>

          <div className="return-detail-items">
            {returnRequest.items?.map((item) => (
              <article className="return-detail-item" key={item.id}>
                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    alt={item.productName}
                  />
                )}

                <div>
                  <h3>{item.productName}</h3>

                  <p>
                    {t("returns.quantity")}: {item.quantity}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/shop/${item.productSlug}`)
                    }
                  >
                    {t("returns.viewProduct")}
                  </button>
                </div>

                <strong>
                  {formatAmount(item.refundAmount)} €
                </strong>
              </article>
            ))}
          </div>
        </section>

        <section className="return-detail-block">
          <h2>{t("returns.trackingTitle")}</h2>

          <div className="return-detail-timeline">
            {timeline.map((step, index) => (
              <div
                className={`return-detail-timeline-step ${
                  step.completed ? "completed" : ""
                }`}
                key={step.key}
              >
                <div className="return-detail-timeline-marker">
                  {step.completed ? "✓" : index + 1}
                </div>

                <div>
                  <strong>{step.label}</strong>
                  <span>{formatDate(step.date)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}